import { useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useQueryClient } from '@tanstack/react-query';
import { DEPENDENCIES_DATA } from '@elementary/common-ui/constants/queryKeys';
import { gql } from 'graphql-request';
import { DependenciesFilterOptions } from 'shared/components/DependenciesTable/types';
import { Dependency } from 'shared/types';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { AssetDisplayType, Totals } from '@elementary/common-ui/types';
import { LatestTestExecution } from '@elementary/common-ui/services/gql/types';
import { useCSVExport } from 'shared/hooks/useCSVExport';

type TableDependencies = {
  asset: {
    dependencies: Dependency[];
  };
};

type ColumnDependencies = {
  asset: {
    column: {
      id: string;
      name: string;
      dependencies: Dependency[];
    };
  };
};

export type NormalizedDependency = {
  direction: string;
  hops: number;
  type: AssetDisplayType;
  id: string;
  name: string;
  isCritical: boolean;
  isRestricted: boolean;
  latestExecutionTimestamp?: string;
  latestExecutionStatus?: string;
  owners: string[];
  tags: string[];
  path: string;
  columnName?: string;
  latestTestExecutions?: LatestTestExecution[];
  testExecutionsTotals?: Totals;
};

const normalizeDependency = (dependency: Dependency): NormalizedDependency => {
  if (dependency.node.__typename === 'RestrictedAsset') {
    return {
      direction: dependency.direction,
      hops: dependency.hops,
      type: dependency.node.displayType,
      id: dependency.node.id ?? '',
      name: '',
      isCritical: false,
      isRestricted: true,
      owners: [],
      tags: [],
      path: '',
    };
  }
  if (dependency.node.parent) {
    return {
      direction: dependency.direction,
      hops: dependency.hops,
      type: dependency.node.parent.displayType,
      id: dependency.node.parent.id,
      name: dependency.node.parent.name,
      columnName: dependency.node.name,
      isCritical: dependency.node.isCritical,
      isRestricted: false,
      latestExecutionTimestamp:
        dependency.node.parent.latestExecution?.executionTimestamp,
      latestExecutionStatus: dependency.node.parent.latestExecution?.status,
      owners: dependency.node.unionedOwners,
      tags: dependency.node.unionedTags,
      path: dependency.node.parent.path,
      latestTestExecutions: dependency.node.parent.latestTestExecutions,
      testExecutionsTotals: dependency.node.parent.testExecutionsTotals,
    };
  }
  return {
    direction: dependency.direction,
    hops: dependency.hops,
    type: dependency.node.displayType,
    id: dependency.node.id,
    name: dependency.node.name,
    isCritical: dependency.node.isCritical,
    isRestricted: false,
    latestExecutionTimestamp:
      dependency.node.latestExecution?.executionTimestamp,
    latestExecutionStatus: dependency.node.latestExecution?.status,
    owners: dependency.node.owners,
    tags: dependency.node.tags,
    path: dependency.node.path,
    latestTestExecutions: dependency.node.latestTestExecutions,
    testExecutionsTotals: dependency.node.testExecutionsTotals,
  };
};

const TableCsvHeaders = [
  { id: 'direction', title: 'Direction' },
  { id: 'hops', title: 'Hops' },
  { id: 'name', title: 'Name' },
  { id: 'isCritical', title: 'Critical Asset' },
  { id: 'type', title: 'Type' },
  { id: 'path', title: 'Path' },
  { id: 'owners', title: 'Owners' },
  { id: 'tags', title: 'Tags' },
];

const ColumnCsvHeaders = [
  { id: 'direction', title: 'Direction' },
  { id: 'hops', title: 'Hops' },
  { id: 'name', title: 'Name' },
  { id: 'isCritical', title: 'Critical Asset' },
  { id: 'type', title: 'Type' },
  { id: 'columnName', title: 'Column Name' },
  { id: 'path', title: 'Path' },
  { id: 'owners', title: 'Owners' },
  { id: 'tags', title: 'Tags' },
];

const DependenciesFilter = {
  [DependenciesFilterOptions.All]: {},
  [DependenciesFilterOptions.WithAccess]: {},
  [DependenciesFilterOptions.Upstream]: { downstreamHops: 0 },
  [DependenciesFilterOptions.Downstream]: {
    upstreamHops: 0,
    assetType: 'TABLE',
  },
  [DependenciesFilterOptions.DownstreamBIAssets]: {
    upstreamHops: 0,
    assetType: 'EXPOSURE',
  },
  [DependenciesFilterOptions.CriticalAssets]: { isCritical: true },
};

export const GetTableDependencies = (includeLatestExecution?: boolean) => gql`
  query GetTableDependencies(
    $id: String!
    $assetType: AssetType
    $upstreamHops: Int
    $downstreamHops: Int
    $isCritical: Boolean
  ) {
    asset(id: $id) {
      dependencies(
        filters: {
          assetType: $assetType
          upstreamHops: $upstreamHops
          downstreamHops: $downstreamHops
          isCritical: $isCritical
        }
      ) {
        hops
        direction
        node {
          __typename
          displayType
          id
          name
          owners
          path
          tags
          isCritical
          ... on TableAsset {
            testExecutionsTotals {
              errors
              failures
              passed
              warnings
            }
            latestTestExecutions {
              test {
                name
              }
              execution {
                status
              }
            }
          ${
            includeLatestExecution
              ? `
            latestExecution {
              executionTimestamp
              status
            }
          `
              : ''
          }
          }
        }
      }
    }
  }
`;

export const GetColumnDependencies = (includeLatestExecution?: boolean) => gql`
  query GetColumnDependencies(
    $id: String!
    $name: String!
    $assetType: AssetType
    $upstreamHops: Int
    $downstreamHops: Int
    $isCritical: Boolean
  ) {
    asset(id: $id) {
      column(name: $name) {
        id
        name
        dependencies(
          filters: {
            assetType: $assetType
            upstreamHops: $upstreamHops
            downstreamHops: $downstreamHops
            isCritical: $isCritical
          }
        ) {
          hops
          direction
          node {
            __typename
            displayType
            id
            ... on ColumnAsset {
              name
              unionedOwners
              unionedTags
              isCritical
              parent {
                id
                displayType
                name
                path
                isCritical
                ... on TableAsset {
                  testExecutionsTotals {
                    errors
                    failures
                    passed
                    warnings
                  }
                  latestTestExecutions {
                    test {
                      name
                    }
                    execution {
                      status
                    }
                  }
             ${
               includeLatestExecution
                 ? `
                  latestExecution {
                    executionTimestamp
                    status
                  }
          `
                 : ''
             }
                }
              }
            }
          }
        }
      }
    }
  }
`;

type Props = {
  tableAssetId?: string;
  resource: {
    type: string;
    name?: string;
  };
  filter: DependenciesFilterOptions;
  includeLatestExecution?: boolean;
};

function useDependenciesData({
  tableAssetId,
  resource,
  filter,
  includeLatestExecution = false,
}: Props) {
  const selectedEnvId = useSelectedEnv();

  const queryClient = useQueryClient();
  const isColumnResource = resource.type === 'COLUMN';

  const { exportToCSV } = useCSVExport({
    headers: isColumnResource ? ColumnCsvHeaders : TableCsvHeaders,
    headerIdDelimiter: '.',
  });

  const getColumnName = (resourceName?: string): string => {
    if (!resourceName) {
      return '';
    }
    const segments = resourceName.split('.');
    return segments[segments.length - 1];
  };
  const columnName = isColumnResource
    ? getColumnName(resource?.name)
    : undefined;

  const queryKey = [
    DEPENDENCIES_DATA,
    selectedEnvId,
    tableAssetId,
    columnName,
    filter,
    includeLatestExecution,
  ];

  const { data, isLoading, isFetching, error } = useGraphqlQuery<
    TableDependencies | ColumnDependencies
  >({
    queryKey,
    query: isColumnResource
      ? GetColumnDependencies(includeLatestExecution)
      : GetTableDependencies(includeLatestExecution),
    variables: {
      id: tableAssetId,
      name: columnName,
      ...DependenciesFilter[filter],
    },
    enabled: !!tableAssetId,
  });

  const normalizedData = useMemo(() => {
    if (data === undefined) {
      return [];
    }
    if (isColumnResource) {
      return (data as ColumnDependencies).asset.column.dependencies.map(
        normalizeDependency,
      );
    }
    return (data as TableDependencies).asset.dependencies.map(
      normalizeDependency,
    );
  }, [data, isColumnResource]);

  const invalidate = () => queryClient.invalidateQueries(queryKey);

  const onCSVDownload = () => {
    exportToCSV(normalizedData, 'dependencies.csv');
  };

  useViewLoadTracking({
    name: 'Dependencies',
    loadDeps: [data],
    contextId: JSON.stringify(queryKey),
  });

  return {
    isLoading,
    isFetching,
    data: normalizedData,
    error,
    invalidate,
    onCSVDownload,
    getColumnName,
  };
}

export default useDependenciesData;
